/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    object, string, bool, array,
} from 'prop-types';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import AddIocIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIocIcon from '@material-ui/icons/RemoveCircleTwoTone';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ProductAttributeIconsQuery from '../ProductAttributeIcon/ProductAttributeIconsQuery';
import { selectedSkuType } from '../../common/types';
import { getBaseCode } from '../../../../../../helpers/tracking/product/dataLayerProductHelpers';

const styles = (theme) => ({
    icons: {
        position: 'absolute',
        right: 10,
        top: 4,
    },
    label: {
        color: theme.palette.cms?.productDetailsSummary || '#607767',
        fontSize: '1.4em',
        [theme.breakpoints.down(600)]: {
            fontSize: '1.1em',
        },
    },
    panelRoot: {
        boxShadow: 'none',
        backgroundColor: 'inherit',
        borderBottom: `2px solid ${theme.palette.cms?.productDetailsSummary || '#607767'}`,
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
            paddingBottom: '10px',
            borderBottom: `2px solid ${theme.palette.cms?.tertiary_hover}`,
        },
    },
    summaryRoot: {
        backgroundColor: 'inherit',
        minHeight: 37,
        padding: 0,
        fontSize: '18px',
        '&.Mui-expanded': {
            margin: 0,
            minHeight: 37,
        },
        '& .MuiExpansionPanelSummary-content': {
            margin: '0 10px',
        },
    },
    summaryContent: {
        '&.Mui-expanded': {
            margin: '0 10px',
        },
    },
    detailsRoot: {
        marginLeft: '15px',
        padding: '0px',
        fontSize: '14px',
        maxHeight: 312,
        overflow: 'auto',
        flexDirection: 'column',
        '& p': {
            fontSize: 16,
        },
        '& ul': {
            marginTop: 0,
            // Mobile
            [theme.breakpoints.down(1025)]: {
                marginTop: 15,
            },
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#808080', // scroll bar color
            borderRadius: 10,
        },
        '&::-webkit-scrollbar': {
            background: 'transparent',
            width: 8,
        },
        scrollbarColor: '#808080 transparent', // for firefox
        scrollbarWidth: 'thin', // for firefox
    },
    outerPanel: {
        borderBottom: '1px solid #d8d8d8 !important',
        '&.Mui-expanded': {
            borderBottom: '1px solid #d8d8d8 !important',
        },
        [theme.breakpoints.up('600')]: {
            maxWidth: '1000px',
            boxShadow: 'none',
            backgroundColor: 'inherit',
            margin: '0 auto !important',
            '&:before': {
                display: 'none',
            },
            '&.Mui-expanded': {
                margin: 0,
                paddingBottom: '10px',
            },
        },
        '& $summaryRoot': {
            backgroundColor: 'inherit',
            '& .MuiExpansionPanelSummary-content': {
                margin: '0px',
                '& h3': {
                    fontWeight: 'normal',
                    textTransform: 'capitalize',
                    fontSize: '24px',
                    lineHeight: '29px',
                    color: '#000000',
                    margin: '28px 0px 24px',
                    display: 'block',
                    [theme.breakpoints.down('600')]: {
                        fontSize: '20px',
                        lineHeight: '25px',
                        margin: '24px 0px 16px',
                    },
                },
            },
        },
        '& ul': {
            marginLeft: '20px',
        },
        '& $detailsRoot>p': {
            fontSize: '18px',
            paddingBottom: '22px',
            [theme.breakpoints.down('600')]: {
                fontSize: '16px',
                paddingBottom: '14px',
            },
        },
        '& ul li': {
            lineHeight: '23px',
            marginBottom: '10px',
            [theme.breakpoints.down('600')]: {
                lineHeight: '21px',
                marginBottom: '5px',
            },
        },
        '& $icons': {
            top: '30px',
            [theme.breakpoints.down('600')]: {
                top: '25px',
            },
        },
    },
    subHeading: {
        fontSize: '18px',
        lineHeight: '23px',
        marginBottom: '10px',
        fontStyle: 'italic',
        [theme.breakpoints.down('600')]: {
            fontSize: '16px',
            lineHeight: '21px',
            marginBottom: '0px',
        },
    },
});

const FoodDetailsExpansionPanel = ({
    classes, summary, details, panelId, expanded, selectedSku, pdpInlineAddon, productAttributes, byoLabelDesign, isPdpRedesignEnabled,
}) => {
    const [contentActive, setContentActive] = useState(expanded);
    const handleChangePanel = (panel) => (event, newExpanded) => {
        setContentActive((newExpanded) ? panel : null);
    };
    const addButton = pdpInlineAddon ? <RemoveIocIcon className={classes.removeIcon} /> : <RemoveIcon />;
    const removeButton = pdpInlineAddon ? <AddIocIcon /> : <AddIcon />;
    return (
        <ExpansionPanel classes={{ root: classes.panelRoot }} className={isPdpRedesignEnabled ? classes.outerPanel : ''} square expanded={contentActive} onChange={handleChangePanel(panelId)}>
            <ExpansionPanelSummary classes={{ root: classes.summaryRoot, content: classes.summaryContent }} aria-controls={`panel-${panelId}`} aria-label={`Toggle Product ${summary}`}>
                <h3
                    className={`${classes.label} ${byoLabelDesign}`}
                >
                    {summary}
                    { isPdpRedesignEnabled  ? <span className={classes.icons}>{(contentActive) ? <ChevronUp color="#2F363D" width={24} height={24} /> : <ChevronDown color="#2F363D" width={24} height={24} />}</span>
                        : <span className={classes.icons}>{(contentActive) ? addButton : removeButton}</span>}
                </h3>
            </ExpansionPanelSummary>
            {selectedSku?.id && isPdpRedesignEnabled && (
                <div className={classes.subHeading}>
                    <span>Product</span> <span>#{getBaseCode(selectedSku.id, true)}</span>
                </div>
            )}
            <ExpansionPanelDetails classes={{ root: classes.detailsRoot }}>
                <ProductAttributeIconsQuery selectedSku={selectedSku} productAttributes={productAttributes} />
                <Typography
                    className={classes.fullWidth}
                >
                    {details}
                </Typography>
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};

FoodDetailsExpansionPanel.propTypes = {
    classes: object.isRequired,
    summary: object.isRequired,
    details: object.isRequired,
    panelId: string.isRequired,
    expanded: bool.isRequired,
    selectedSku: selectedSkuType,
    pdpInlineAddon: bool,
    productAttributes: array,
    byoLabelDesign: string,
    isPdpRedesignEnabled: bool,
};
FoodDetailsExpansionPanel.defaultProps = {
    selectedSku: {},
    pdpInlineAddon: false,
    productAttributes: [],
    byoLabelDesign: '',
    isPdpRedesignEnabled: false,
};
export default withStyles(styles)(FoodDetailsExpansionPanel);
